/**
 * NotificationSettingsPanel — super-admin master toggles for ALL app
 * notifications (in-app + push + WhatsApp), gated at the dispatcher.
 */
import { useEffect, useState } from 'react';
import { Bell, BellOff, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card } from './ui/card';
import { Switch } from './ui/switch';

export default function NotificationSettingsPanel() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get('/admin/notification-settings')
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data) return null;

  const { toggles, catalog } = data;
  const offCount = catalog.filter((c) => !toggles[c.key]).length + (toggles.master ? 0 : 1);

  const setToggle = async (key, value) => {
    const prev = { ...toggles };
    setData((d) => ({ ...d, toggles: { ...d.toggles, [key]: value } }));
    try {
      const r = await api.put('/admin/notification-settings', { toggles: { [key]: value } });
      setData((d) => ({ ...d, toggles: r.data.toggles }));
      toast.success(value ? 'Notifications ON' : 'Notifications OFF');
    } catch (e) {
      setData((d) => ({ ...d, toggles: prev }));
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
  };

  return (
    <Card className="p-4 border-2 rounded-2xl" data-testid="notification-settings-panel">
      <button
        className="w-full flex items-center justify-between gap-2"
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-settings-toggle"
      >
        <div className="flex items-center gap-2">
          {toggles.master ? <Bell className="w-4 h-4 text-primary" /> : <BellOff className="w-4 h-4 text-red-500" />}
          <span className="font-display font-bold text-sm uppercase tracking-[0.14em]">Notification Controls</span>
          {offCount > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-600 dark:text-red-400 font-bold" data-testid="notification-off-count">
              {offCount} OFF
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-3 space-y-1" data-testid="notification-settings-body">
          <div className="flex items-center justify-between gap-3 py-2.5 px-3 rounded-xl bg-primary/5 border border-primary/20">
            <div>
              <p className="text-sm font-bold">🔔 All notifications</p>
              <p className="text-xs text-muted-foreground">Master kill-switch — OFF silences everything (WhatsApp, in-app bell, push) app-wide</p>
            </div>
            <Switch
              checked={toggles.master}
              onCheckedChange={(v) => setToggle('master', v)}
              data-testid="notif-toggle-master"
            />
          </div>

          {catalog.map((c) => (
            <div
              key={c.key}
              className={`flex items-center justify-between gap-3 py-2 px-3 rounded-xl ${!toggles.master ? 'opacity-40' : ''}`}
              data-testid={`notif-row-${c.key}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold">{c.emoji} {c.label}</p>
                <p className="text-xs text-muted-foreground leading-snug">{c.description}</p>
              </div>
              <Switch
                checked={toggles[c.key]}
                onCheckedChange={(v) => setToggle(c.key, v)}
                disabled={!toggles.master}
                data-testid={`notif-toggle-${c.key}`}
              />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
